import { ref, onMounted, onUnmounted } from 'vue'
import { createLogger } from '@/utils/logger'
import { captureError } from '@/utils/sentry'

const logger = createLogger('PWA')

const DEV_PWA_OVERRIDE_KEY = 'dev-pwa-installed'
const DISPLAY_MODE_STANDALONE = '(display-mode: standalone)'

export interface PWAInstallPrompt extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function usePWA() {
  const isInstalled = ref(false)
  const isInstallable = ref(false)
  const deferredPrompt = ref<PWAInstallPrompt | null>(null)
  const isIOS = ref(false)
  const isStandalone = ref(false)

  // Check for dev mode override ONLY in development environment
  const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development'

  const checkInstallStatus = () => {
    if (isDevelopment) {
      const devOverride = localStorage.getItem(DEV_PWA_OVERRIDE_KEY)
      if (devOverride === 'true') {
        logger.info('DEV MODE: PWA installed override enabled')
        isInstalled.value = true
        isStandalone.value = true
        return
      } else if (devOverride === 'false') {
        logger.info('DEV MODE: PWA installed override disabled')
        isInstalled.value = false
        isStandalone.value = false
        return
      }
    }

    // Production logic: Check if running in standalone mode (installed PWA)
    const matchesDisplayMode = window.matchMedia(DISPLAY_MODE_STANDALONE).matches
    const navigatorStandalone = (window.navigator as { standalone?: boolean }).standalone === true
    isStandalone.value = matchesDisplayMode || navigatorStandalone

    // Check if it's iOS
    isIOS.value = /iPad|iPhone|iPod/.test(navigator.userAgent)

    // PWA is installed if running in standalone mode (both iOS and other browsers)
    isInstalled.value = isStandalone.value

    logger.info('Install status checked:', {
      isInstalled: isInstalled.value,
      isStandalone: isStandalone.value,
      matchesDisplayMode,
      navigatorStandalone,
      isIOS: isIOS.value,
      isDevelopment,
    })
  }

  const handleBeforeInstallPrompt = (e: Event) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault()
    // Stash the event so it can be triggered later
    deferredPrompt.value = e as PWAInstallPrompt
    isInstallable.value = true
  }

  const handleAppInstalled = () => {
    isInstalled.value = true
    isInstallable.value = false
    deferredPrompt.value = null
  }

  const installPWA = async (): Promise<boolean> => {
    if (!deferredPrompt.value) {
      return false
    }

    try {
      // Show the install prompt
      await deferredPrompt.value.prompt()

      // Wait for the user to respond to the prompt
      const choiceResult = await deferredPrompt.value.userChoice

      if (choiceResult.outcome === 'accepted') {
        isInstalled.value = true
        isInstallable.value = false
      }

      deferredPrompt.value = null
      return choiceResult.outcome === 'accepted'
    } catch (error) {
      logger.error('Error installing PWA:', error)
      captureError(error instanceof Error ? error : new Error(String(error)), { source: 'usePWA.installPWA' })
      return false
    }
  }

  const canInstall = (): boolean => {
    return !isInstalled.value && (isInstallable.value || isIOS.value)
  }

  // Event handlers hoisted for cleanup
  const handlePWAModeChange = () => {
    checkInstallStatus()
  }
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === DEV_PWA_OVERRIDE_KEY) {
      checkInstallStatus()
    }
  }
  let mediaQuery: MediaQueryList | null = null
  const handleDisplayModeChange = (e: MediaQueryListEvent) => {
    isStandalone.value = e.matches
    isInstalled.value = e.matches
  }

  onMounted(() => {
    // Clean up dev overrides in production builds
    if (!isDevelopment) {
      const devOverride = localStorage.getItem(DEV_PWA_OVERRIDE_KEY)
      if (devOverride !== null) {
        logger.info('PRODUCTION: Removing dev-pwa-installed override')
        localStorage.removeItem(DEV_PWA_OVERRIDE_KEY)
      }
    }

    checkInstallStatus()

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // Listen for the appinstalled event
    window.addEventListener('appinstalled', handleAppInstalled)

    // Listen for PWA mode changes (dev mode toggle)
    window.addEventListener('pwa-mode-change', handlePWAModeChange)

    // Listen for storage changes (cross-tab dev mode changes)
    window.addEventListener('storage', handleStorageChange)

    // Check for display mode changes
    mediaQuery = window.matchMedia(DISPLAY_MODE_STANDALONE)
    mediaQuery.addEventListener('change', handleDisplayModeChange)
  })

  onUnmounted(() => {
    // Remove all event listeners to prevent memory leaks
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.removeEventListener('appinstalled', handleAppInstalled)
    window.removeEventListener('pwa-mode-change', handlePWAModeChange)
    window.removeEventListener('storage', handleStorageChange)
    mediaQuery?.removeEventListener('change', handleDisplayModeChange)
  })

  return {
    isInstalled,
    isInstallable,
    isIOS,
    isStandalone,
    installPWA,
    canInstall,
  }
}
